import { fetchApi } from "@/lib/api/index.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface DemoAccount {
  key: string;
  displayName: string;
  summary: string;
  phone: string;
  canUseDashboard: boolean;
  systemRoles: string[];
  userId: string | null;
  hasOnboarded: boolean | null;
  seeded: boolean;
}

const DEMO_ACCOUNTS_KEY = ["demo-accounts"] as const;

export interface StartDemoLoginResult {
  // Stytch's `phone_id`
  method_id: string;
  phone: string;
}

// Send the login code for an account.
export const useStartDemoLogin = () =>
  useMutation({
    mutationFn: async (key: string) => {
      const { data } = await fetchApi<StartDemoLoginResult>(
        { url: `/v2/demo-accounts/${key}/start-login`, method: "POST" },
        false,
      );

      return data;
    },
  });

export const useDemoAccounts = () =>
  useQuery({
    queryKey: DEMO_ACCOUNTS_KEY,
    queryFn: async () => {
      const { data } = await fetchApi<{ accounts: DemoAccount[] }>(
        { url: "/v2/demo-accounts", method: "GET" },
        false,
      );

      return data?.accounts ?? [];
    },
    retry: false,
    staleTime: 30_000,
  });

export interface ProvisionDemoAccountsInput {
  keys?: string[];
  fresh?: boolean;
}

/**
 * `fresh` wipes accumulated test activity first
 */
export const useProvisionDemoAccounts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProvisionDemoAccountsInput = {}) => {
      const { data } = await fetchApi<{ accounts: DemoAccount[] }>({
        url: "/v2/demo-accounts/provision",
        method: "POST",
        data: input,
      });

      return data?.accounts ?? [];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEMO_ACCOUNTS_KEY });
    },
  });
};
